import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { CircleCheck, CircleDashed, Network } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

type Paso = { clave: string; titulo: string; hecho: boolean; detalle: string }
type Ingreso = {
  persona_id: number
  persona: string
  cargo: string | null
  fecha_ingreso: string | null
  pasos: Paso[]
  completos: number
  hoja_de_ruta: { dia: number; actividad: string; responsable: string }[]
  avance: Record<string, string>
}

/** Dónde se hace cada paso, para ir directo. */
/** Una fila por trabajador: cuántos pasos lleva y cuáles le faltan. */
function documentoDeIngresos(ingresos: Ingreso[]): DocumentoExportable {
  return {
    titulo: "Ingresos del personal",
    subtitulo: "Pasos del ingreso de cada trabajador y su hoja de ruta",
    secciones: [
      {
        titulo: "Ingresos del personal",
        columnas: [
          { titulo: "Trabajador", tipo: "texto" },
          { titulo: "Cargo", tipo: "texto" },
          { titulo: "Ingreso", tipo: "fecha" },
          { titulo: "Pasos completos", tipo: "numero" },
          { titulo: "Pasos totales", tipo: "numero" },
          { titulo: "Lo que falta", tipo: "texto" },
          { titulo: "Hoja de ruta hecha", tipo: "numero" },
          { titulo: "Hoja de ruta total", tipo: "numero" },
        ],
        filas: ingresos.map((i) => ({
          celdas: [
            i.persona,
            i.cargo,
            i.fecha_ingreso ? i.fecha_ingreso.slice(0, 10) : null,
            i.completos,
            i.pasos.length,
            i.pasos
              .filter((p) => !p.hecho)
              .map((p) => p.titulo)
              .join(", ") || null,
            i.hoja_de_ruta.filter((_, n) => i.avance[String(n)] !== undefined).length,
            i.hoja_de_ruta.length,
          ],
        })),
      },
    ],
  }
}

const DONDE: Record<string, (id: number) => string> = {
  ficha: (id) => `/talento-humano/personas/${id}`,
  documentos: (id) => `/talento-humano/personas/${id}`,
  contrato: (id) => `/talento-humano/personas/${id}`,
  hoja: () => "/planificacion/cargos",
  nomina: () => "/talento-humano/contratos",
  cuenta: () => "/admin/usuarios",
  organigrama: () => "/planificacion/organigrama",
}

/**
 * Talento Humano → Ingresos (28/10, minuta «Estructura de la Compañía», flujo de ingreso):
 * en qué paso va cada trabajador —ficha, documentos, contrato, hoja de ruta, nómina,
 * cuenta y organigrama— y su hoja de ruta para ir marcándola.
 *
 * ⚠ **Los pasos los deduce el servidor de lo que ya existe** (`ingreso_personal.py`): aquí
 * no se marcan, se van a hacer. Lo único que se hace desde aquí es ubicarlo en el
 * organigrama y marcar las actividades de su hoja de ruta.
 */
export function IngresosTH() {
  const usuario = usarSesion((e) => e.usuario)
  const puedeOrg = tienePermiso(usuario, "organigrama:editar")
  const puedeMarcar = tienePermiso(usuario, "persona:administrar")
  const clienteQuery = useQueryClient()
  const [abierta, setAbierta] = useState<number | null>(null)

  const consulta = useQuery({
    queryKey: ["ingresos-personal"],
    queryFn: () => api.get<Ingreso[]>("/api/v1/talento-humano/ingresos"),
  })
  const refrescar = () => clienteQuery.invalidateQueries({ queryKey: ["ingresos-personal"] })
  const ubicar = useMutation({
    mutationFn: (id: number) => api.post(`/api/v1/talento-humano/ingresos/${id}/organigrama`, {}),
    onSuccess: refrescar,
  })
  const marcar = useMutation({
    mutationFn: (v: { id: number; indice: number; hecha: boolean }) =>
      api.put(`/api/v1/talento-humano/ingresos/${v.id}/hoja-de-ruta`, {
        indice: v.indice,
        hecha: v.hecha,
      }),
    onSuccess: refrescar,
  })

  if (consulta.isPending) return <EstadoCarga etiqueta="Cargando los ingresos" />
  if (consulta.isError) {
    return (
      <EstadoError
        titulo="No se pudo cargar"
        error={consulta.error}
        onReintentar={() => consulta.refetch()}
      />
    )
  }
  const error = ubicar.error ?? marcar.error

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cada paso sale de lo que ya está cargado. Los que faltan llevan a donde se hacen.
        </p>
        {consulta.data.length > 0 && (
          <Exportar
            titulo="Ingresos del personal"
            documento={() => documentoDeIngresos(consulta.data)}
          />
        )}
      </div>
      {error && (
        <p role="alert" className="tarjeta p-3 text-sm" style={{ color: "var(--destructive)" }}>
          {(error as Error).message}
        </p>
      )}
      <ul className="space-y-2">
        {consulta.data.map((i) => {
          const abierto = abierta === i.persona_id
          const enOrg = i.pasos.find((p) => p.clave === "organigrama")?.hecho
          return (
            <li key={i.persona_id} className="tarjeta p-0">
              <button
                type="button"
                aria-expanded={abierto}
                onClick={() => setAbierta(abierto ? null : i.persona_id)}
                className="foco-cristal flex w-full flex-wrap items-center gap-3 p-4 text-left"
              >
                <span className="min-w-0 flex-1">
                  <span className="font-semibold">{i.persona}</span>
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    {i.cargo ?? "Sin cargo"}
                    {i.fecha_ingreso && ` · ingresó el ${comoFecha(i.fecha_ingreso)}`}
                  </span>
                </span>
                <span className="flex gap-1" aria-hidden={true}>
                  {i.pasos.map((p) =>
                    p.hecho ? (
                      <CircleCheck key={p.clave} size={16} style={{ color: "var(--success)" }} />
                    ) : (
                      <CircleDashed key={p.clave} size={16} style={{ color: "var(--fg-muted)" }} />
                    ),
                  )}
                </span>
                <span className="text-xs tabular-nums">
                  {i.completos} de {i.pasos.length}
                </span>
              </button>
              {abierto && (
                <div
                  className="space-y-4 border-t px-4 pt-3 pb-4"
                  style={{ borderColor: "var(--border)" }}
                >
                  <ol className="space-y-1.5">
                    {i.pasos.map((p) => (
                      <li key={p.clave} className="flex flex-wrap items-center gap-2 text-sm">
                        {p.hecho ? (
                          <CircleCheck
                            size={16}
                            style={{ color: "var(--success)" }}
                            aria-label="Hecho"
                          />
                        ) : (
                          <CircleDashed
                            size={16}
                            style={{ color: "var(--fg-muted)" }}
                            aria-label="Pendiente"
                          />
                        )}
                        <span className="font-medium">{p.titulo}</span>
                        <span style={{ color: "var(--fg-muted)" }}>· {p.detalle}</span>
                        {!p.hecho && (
                          <Link
                            to={DONDE[p.clave]?.(i.persona_id) ?? "#"}
                            className="foco-cristal text-xs underline"
                          >
                            Hacerlo
                          </Link>
                        )}
                      </li>
                    ))}
                  </ol>
                  {!enOrg && puedeOrg && i.cargo && (
                    <Boton
                      tamano="sm"
                      disabled={ubicar.isPending}
                      onClick={() => ubicar.mutate(i.persona_id)}
                    >
                      <Network size={14} /> Ubicarlo en el organigrama
                    </Boton>
                  )}
                  {i.hoja_de_ruta.length > 0 && (
                    <fieldset className="space-y-1">
                      <legend className="mb-1 text-sm font-semibold">Su hoja de ruta</legend>
                      {i.hoja_de_ruta.map((a, n) => {
                        const fecha = i.avance[String(n)]
                        return (
                          <label
                            key={`${a.dia}-${a.actividad}`}
                            className="flex items-start gap-2 text-sm"
                          >
                            <input
                              type="checkbox"
                              className="mt-1"
                              disabled={!puedeMarcar || marcar.isPending}
                              checked={fecha !== undefined}
                              onChange={(e) =>
                                marcar.mutate({
                                  id: i.persona_id,
                                  indice: n,
                                  hecha: e.target.checked,
                                })
                              }
                            />
                            <span>
                              <span className="tabular-nums" style={{ color: "var(--fg-muted)" }}>
                                Día {a.dia} ·{" "}
                              </span>
                              {a.actividad}
                              {a.responsable && ` · con ${a.responsable}`}
                              {fecha && (
                                <span className="text-xs" style={{ color: "var(--success)" }}>
                                  {" "}
                                  · hecho el {comoFecha(fecha)}
                                </span>
                              )}
                            </span>
                          </label>
                        )
                      })}
                    </fieldset>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
